import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import {
  DirectPaymentOrderSchema,
  ORDER_REASON_MAX_LENGTH,
} from '../direct-payment-order/direct-payment-order';
import { type DirectPaymentOrderStatus } from '../direct-payment-order-status/direct-payment-order-status';
import { ProductKeySchema } from '../product-key/product-key';

/**
 * The receipt the member sent, as the phone needs to draw it back (F3): the
 * asset to mint a read for, and the two facts that decide whether it is drawn
 * as a picture or as a document tile without a round trip to find out.
 */
export const MemberDirectOrderReceiptSchema = z
  .object({
    mediaId: UuidSchema,
    mime: z.string().min(1),
    sizeBytes: z.number().int().nonnegative(),
  })
  .strict();

export type MemberDirectOrderReceipt = z.infer<typeof MemberDirectOrderReceiptSchema>;

/**
 * A direct-payment order as its own member sees it (F3).
 *
 * `.pick()` from the staff schema, never `.omit()` — the P22b4 leak: an
 * allow-list cannot inherit a field somebody later adds to the base. What is
 * kept is what the member may act on or must be told: the money (their own
 * terms, discount included), the receipt they sent, the standing and — on a
 * refusal — the reason verbatim, because "your payment was not accepted" with
 * no why is a support ticket. What is dropped is the staff side: the grants
 * claimed, who decided, the operator's note.
 */
export const MemberDirectOrderSchema = DirectPaymentOrderSchema.pick({
  id: true,
  productKey: true,
  kind: true,
  status: true,
  listAmount: true,
  discountAmount: true,
  chargedAmount: true,
  currency: true,
  submittedAt: true,
  confirmedAt: true,
  rejectedAt: true,
})
  .extend({
    /**
     * The product's name in the member's locale — what the screen is titled
     * with. `null` when the plan has since been withdrawn: the order outlives
     * it, and the screen then says "your payment" rather than a key.
     */
    productName: z.string().nullable(),
    /** `null` on a staff-keyed order whose receipt never came through the phone. */
    receipt: MemberDirectOrderReceiptSchema.nullable(),
    decisionReason: z.string().max(ORDER_REASON_MAX_LENGTH).nullable(),
  })
  .strict();

export type MemberDirectOrder = z.infer<typeof MemberDirectOrderSchema>;

/**
 * The member's whole submission: which plan, and the receipt already uploaded
 * and finalized as a `payment_receipt`. The price is resolved server-side at the
 * member's own scope (U15) and the method is the transfer the account panel
 * described — neither is the member's to name.
 */
export const MemberDirectOrderSubmitSchema = z
  .object({
    productKey: ProductKeySchema,
    mediaId: UuidSchema,
  })
  .strict();

export type MemberDirectOrderSubmitRequest = z.infer<typeof MemberDirectOrderSubmitSchema>;

export const MemberDirectOrdersListQuerySchema = z
  .object({
    productKey: ProductKeySchema.optional(),
  })
  .strict();

export type MemberDirectOrdersListQuery = z.infer<typeof MemberDirectOrdersListQuerySchema>;

const OPEN_STATUSES: ReadonlySet<DirectPaymentOrderStatus> = new Set<DirectPaymentOrderStatus>([
  'draft',
  'awaiting_confirmation',
]);

/**
 * Whether this order still holds the member's (product) slot — the api's
 * partial unique index says the same thing on the storage side, so a client
 * asking "may I submit another?" and the server refusing agree by construction.
 */
export function isOpenMemberDirectOrder(order: Pick<MemberDirectOrder, 'status'>): boolean {
  return OPEN_STATUSES.has(order.status);
}
